import React, { createContext, useCallback, useContext, useState } from 'react';
import { createPortal } from 'react-dom';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastRecord {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  show: (type: ToastType, message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

// Error/warning hang longer so users can read; success/info dismiss quickly.
const DURATIONS: Record<ToastType, number> = {
  success: 3000,
  info:    3000,
  warning: 5000,
  error:   5000,
};

// Tone palettes mirror existing banner patterns (MyPage Cancel dialog,
// CryptoPayment warnings) — keeps the whole site visually coherent.
const TONE: Record<ToastType, { bg: string; border: string; text: string; icon: string; path: string }> = {
  success: {
    bg: 'bg-forest-50', border: 'border-forest-200', text: 'text-forest-800', icon: 'text-forest-600',
    path: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  error: {
    bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'text-red-500',
    path: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z',
  },
  warning: {
    bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: 'text-amber-500',
    path: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z',
  },
  info: {
    bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: 'text-amber-600',
    path: 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
  },
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((type: ToastType, message: string) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => [...prev, { id, message, type }]);
    window.setTimeout(() => dismiss(id), DURATIONS[type]);
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <div className="fixed top-4 right-4 sm:top-6 sm:right-6 z-[100] flex flex-col gap-2 max-w-sm w-[calc(100vw-2rem)] sm:w-96 pointer-events-none">
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
};

const ToastItem: React.FC<{ toast: ToastRecord; onDismiss: () => void }> = ({ toast, onDismiss }) => {
  const t = TONE[toast.type];
  return (
    <div
      role="alert"
      className={`flex items-start gap-2.5 p-3 rounded-xl border shadow-card pointer-events-auto animate-slide-in-right ${t.bg} ${t.border}`}
    >
      <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${t.icon}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d={t.path} />
      </svg>
      <p className={`text-xs leading-relaxed flex-1 whitespace-pre-line ${t.text}`}>{toast.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className={`flex-shrink-0 ${t.icon} hover:opacity-70 transition-opacity cursor-pointer`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return {
    success: (msg: string) => ctx.show('success', msg),
    error:   (msg: string) => ctx.show('error', msg),
    warning: (msg: string) => ctx.show('warning', msg),
    info:    (msg: string) => ctx.show('info', msg),
  };
};
